import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaCalendarAlt,
  FaTrophy,
  FaUsers,
  FaHeart,
  FaStar,
  FaHistory,
  FaFutbol,
  FaAward,
  FaHandshake,
  FaRocket,
  FaEye
} from 'react-icons/fa';
import axios from 'axios';
import Navbar from '../components/Layout/Navbar';
import TopBar from '../components/Layout/TopBar';

const Club = () => {
  const [clubData, setClubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('histoire');

  useEffect(() => {
    fetchClubData();
  }, []);

  const fetchClubData = async () => {
    try {
      const response = await axios.get('https://api-dofa.fff.fr/api/clubs/186901.json');
      setClubData(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des données du club:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateGoogleMapsUrl = (terrain) => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      console.warn('Google Maps API key not found. Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file');
      return '';
    }

    if (terrain.latitude && terrain.longitude) {
      return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${terrain.latitude},${terrain.longitude}&zoom=15`;
    }
    return `https://www.google.com/maps/embed/v1/search?key=${apiKey}&q=${encodeURIComponent(terrain.address + ' ' + terrain.city)}`;
  };

  const tabs = [
    { id: 'histoire', name: 'Notre Histoire', icon: FaHistory },
    { id: 'valeurs', name: 'Nos Valeurs', icon: FaHeart },
    { id: 'palmares', name: 'Palmarès', icon: FaTrophy },
    { id: 'contact', name: 'Contact', icon: FaPhone },
    { id: 'installations', name: 'Nos Stades', icon: FaMapMarkerAlt }
  ];

  const palmares = [
    { title: 'Champion DH Nord', year: '2018', icon: FaTrophy, color: 'text-[#FFE400]' },
    { title: 'Finaliste Coupe du Nord', year: '2019', icon: FaAward, color: 'text-[#BDFF00]' },
    { title: 'Vainqueur Coupe Hainaut', year: '2020', icon: FaTrophy, color: 'text-[#FFE400]' },
    { title: 'Label Jeunes FFF', year: '2021', icon: FaStar, color: 'text-[#BDFF00]' },
    { title: 'Montée en National 2', year: '2022', icon: FaRocket, color: 'text-[#FFE400]' }
  ];

  const valeurs = [
    {
      title: 'Passion',
      icon: FaHeart,
      description: 'L\'amour du football guide chacune de nos actions, de la formation des jeunes à l\'excellence sportive.',
      gradient: 'from-red-500 to-pink-500'
    },
    {
      title: 'Solidarité',
      icon: FaHandshake,
      description: 'Un esprit d\'équipe et d\'entraide qui unit joueurs, éducateurs, dirigeants et supporters.',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Excellence',
      icon: FaTrophy,
      description: 'La recherche constante du progrès sportif et humain dans le respect des valeurs du sport.',
      gradient: 'from-[#FFE400] to-[#BDFF00]'
    }
  ];

  const stats = [
    { label: 'Années d\'histoire', value: '92', icon: FaCalendarAlt },
    { label: 'Équipes', value: '12', icon: FaUsers },
    { label: 'Licenciés', value: '300+', icon: FaFutbol },
    { label: 'Trophées', value: '15', icon: FaTrophy }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#002a7a] to-[#001B56]">
        <TopBar />
        <div className="sticky top-4 z-50 flex flex-col items-center w-full pt-10">
          <div className="relative w-[90vw] max-w-2xl">
            <Navbar />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-16">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="inline-block rounded-full h-16 w-16 border-4 border-[#FFE400] border-t-transparent mx-auto mb-4"
            />
            <p className="text-xl text-blue-200">Chargement des informations du club...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#002a7a] to-[#001B56]">
      <TopBar />
      <div className="sticky top-4 z-50 flex flex-col items-center w-full pt-10">
        <div className="relative w-[90vw] max-w-2xl">
          <Navbar />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="backdrop-blur-md bg-white/5 rounded-2xl p-8 border border-white/10 relative overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-10 left-10 text-8xl">⚽</div>
              <div className="absolute bottom-10 right-10 text-8xl">🏆</div>
              <div className="absolute top-1/2 left-1/4 text-6xl">⭐</div>
            </div>

            <div className="relative z-10 text-center">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
                className="flex items-center justify-center gap-4 mb-6"
              >
                <div className="p-4 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-2xl shadow-lg">
                  <FaFutbol className="text-4xl text-[#001B56]" />
                </div>
                <h1 className="text-4xl md:text-6xl font-bold text-white">
                  ENTENTE FEIGNIES AULNOYE FC
                </h1>
              </motion.div>

              <p className="text-xl text-blue-300/70 mb-8 max-w-3xl mx-auto">
                Football Club depuis 2016 - Passion, Excellence et Tradition 🔥
              </p>

              {/* Stats rapides */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="backdrop-blur-sm bg-white/5 rounded-xl p-4 border border-white/10 hover:border-white/20 transition-colors"
                  >
                    <div className="flex items-center justify-center mb-2">
                      <stat.icon className="text-2xl text-[#FFE400]" />
                    </div>
                    <div className="text-2xl font-bold text-white">{stat.value}</div>
                    <div className="text-sm text-blue-300/60">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Navigation par onglets */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="backdrop-blur-md bg-white/5 rounded-xl p-2 border border-white/10">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <motion.button
                    key={tab.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl transition-all duration-300 ${activeTab === tab.id
                        ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] shadow-lg'
                        : 'text-white hover:bg-white/20'
                      }`}
                  >
                    <IconComponent className="text-lg" />
                    <span className="font-medium">{tab.name}</span>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Contenu des onglets */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Histoire du club */}
          {activeTab === 'histoire' && (
            <div className="backdrop-blur-md bg-white/5 rounded-2xl p-8 border border-white/10">
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-xl">
                  <FaHistory className="text-2xl text-[#001B56]" />
                </div>
                <h2 className="text-2xl font-bold text-white">Notre Histoire</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-6 text-blue-200 text-lg leading-relaxed">
                  <p>
                    L'<strong className="text-[#BDFF00]">Entente Feignies Aulnoye Football Club</strong> naît en 2016 de la fusion
                    de deux clubs historiques de la région, le SC Feignies (1951) et l'AS Aulnoye (1934). Depuis plus d'un siècle, notre club incarne les valeurs du
                    football amateur : passion, solidarité et excellence sportive.
                  </p>
                  <p>
                    Évoluant actuellement en <strong className="text-[#FFE400]">National 2</strong>, l'EFAFC forme
                    des générations de footballeurs dans un esprit familial et convivial. Notre centre de formation
                    accueille plus de 200 jeunes joueurs de 6 à 18 ans.
                  </p>
                  <p>
                    Le club rayonne sur tout le territoire du Hainaut et s'illustre régulièrement dans les compétitions
                    régionales et nationales grâce à ses équipes seniors, jeunes et féminines.
                  </p>
                </div>

                <div className="backdrop-blur-sm bg-white/20 rounded-3xl p-8 border border-white/30">
                  <h3 className="text-2xl font-bold text-[#FFE400] mb-6 flex items-center gap-3">
                    <FaAward className="text-[#FFE400]" />
                    Moments Clés
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-white/10 rounded-xl">
                      <div className="w-12 h-12 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-full flex items-center justify-center text-[#001B56] font-bold">
                        2016
                      </div>
                      <span className="text-white">Fondation du club</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white/10 rounded-xl">
                      <div className="w-12 h-12 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-full flex items-center justify-center text-[#001B56] font-bold">
                        2018
                      </div>
                      <span className="text-white">Montée en National</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white/10 rounded-xl">
                      <div className="w-12 h-12 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-full flex items-center justify-center text-[#001B56] font-bold">
                        2021
                      </div>
                      <span className="text-white">Label Jeunes FFF</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Nos Valeurs */}
          {activeTab === 'valeurs' && (
            <div className="backdrop-blur-md bg-white/10 rounded-3xl p-8 border border-white/20 shadow-2xl">
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-2xl">
                  <FaHeart className="text-2xl text-[#001B56]" />
                </div>
                <h2 className="text-3xl font-bold text-white">Nos Valeurs</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {valeurs.map((valeur, index) => {
                  const IconComponent = valeur.icon;
                  return (
                    <motion.div
                      key={valeur.title}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.2 }}
                      whileHover={{ y: -10 }}
                      className="backdrop-blur-sm bg-white/20 rounded-3xl p-8 border border-white/30 text-center group hover:shadow-2xl transition-all duration-500"
                    >
                      <div className="w-20 h-20 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="text-3xl text-[#001B56]" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-4">{valeur.title}</h3>
                      <p className="text-blue-200 leading-relaxed">{valeur.description}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Palmarès */}
          {activeTab === 'palmares' && (
            <div className="backdrop-blur-md bg-white/10 rounded-3xl p-8 border border-white/20 shadow-2xl">
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-2xl">
                  <FaTrophy className="text-2xl text-[#001B56]" />
                </div>
                <h2 className="text-3xl font-bold text-white">Palmarès</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {palmares.map((achievement, index) => {
                  const IconComponent = achievement.icon;
                  return (
                    <motion.div
                      key={achievement.title}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                      className="backdrop-blur-sm bg-white/20 rounded-2xl p-6 border border-white/30 hover:shadow-xl transition-all duration-300"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-xl">
                          <IconComponent className={`text-2xl text-[#001B56]`} />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-white">{achievement.title}</h3>
                          <p className={`text-sm ${achievement.color} font-medium`}>{achievement.year}</p>
                        </div>
                        <div className="text-2xl font-bold text-[#FFE400]">#{index + 1}</div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Contact */}
          {activeTab === 'contact' && (
            <div className="backdrop-blur-md bg-white/10 rounded-3xl p-8 border border-white/20 shadow-2xl">
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-2xl">
                  <FaPhone className="text-2xl text-[#001B56]" />
                </div>
                <h2 className="text-3xl font-bold text-white">Nous Contacter</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <motion.div
                  whileHover={{ y: -10 }}
                  className="backdrop-blur-sm bg-white/20 rounded-3xl p-8 text-center border border-white/30 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-full flex items-center justify-center mx-auto mb-6">
                    <FaMapMarkerAlt className="text-[#001B56] text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">Adresse</h3>
                  <p className="text-blue-200">
                    {clubData?.address2}<br />
                    {clubData?.postal_code} {clubData?.location}
                  </p>
                </motion.div>

                <motion.div
                  whileHover={{ y: -10 }}
                  className="backdrop-blur-sm bg-white/20 rounded-3xl p-8 text-center border border-white/30 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-full flex items-center justify-center mx-auto mb-6">
                    <FaPhone className="text-[#001B56] text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">Téléphone</h3>
                  <p className="text-blue-200">
                    {clubData?.membres?.find(m => m.individu?.contacts?.some(c => c.co_typ_coor === 'TA'))?.individu?.contacts?.find(c => c.co_typ_coor === 'TA')?.co_coor || '03 27 67 34 52'}
                  </p>
                </motion.div>

                <motion.div
                  whileHover={{ y: -10 }}
                  className="backdrop-blur-sm bg-white/20 rounded-3xl p-8 text-center border border-white/30 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-full flex items-center justify-center mx-auto mb-6">
                    <FaEnvelope className="text-[#001B56] text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">Email</h3>
                  <p className="text-blue-200">
                    {clubData?.contacts?.find(c => c.type === 'EO')?.value || 'contact@efafc.fr'}
                  </p>
                </motion.div>
              </div>

              {/* Call to Action */}
              <div className="mt-12">
                <div className="backdrop-blur-sm bg-gradient-to-r from-[#FFE400]/20 to-[#BDFF00]/20 rounded-3xl p-8 border border-[#FFE400]/30 text-center">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Rejoignez l'aventure EFAFC ! 🚀
                  </h3>
                  <p className="text-blue-200 text-lg mb-6">
                    Que vous soyez joueur, supporter ou bénévole, votre place est ici.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-8 py-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] font-bold rounded-xl hover:shadow-xl transition-all duration-300"
                    >
                      Nous Contacter
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-8 py-3 border-2 border-[#FFE400] text-[#FFE400] font-bold rounded-xl hover:bg-[#FFE400] hover:text-[#001B56] transition-all duration-300"
                    >
                      Découvrir nos Équipes
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Nos Installations */}
          {activeTab === 'installations' && (
            <div className="backdrop-blur-md bg-white/10 rounded-3xl p-8 border border-white/20 shadow-2xl">
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-2xl">
                  <FaMapMarkerAlt className="text-2xl text-[#001B56]" />
                </div>
                <h2 className="text-3xl font-bold text-white">Nos Installations</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {clubData?.terrains?.map((terrain, index) => (
                  <motion.div
                    key={terrain.te_no}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2 }}
                    whileHover={{ y: -5 }}
                    className="backdrop-blur-sm bg-white/20 rounded-3xl overflow-hidden border border-white/30 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-2 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-xl">
                          <FaFutbol className="text-[#001B56] text-xl" />
                        </div>
                        <h3 className="text-xl font-bold text-white">{terrain.name}</h3>
                      </div>

                      <div className="flex items-center gap-2 text-blue-200 mb-2">
                        <FaMapMarkerAlt className="text-[#FFE400]" />
                        <span>{terrain.address}, {terrain.city}</span>
                      </div>

                      <div className="flex items-center gap-2 text-blue-200 mb-4">
                        <FaEye className="text-[#BDFF00]" />
                        <span>Surface : {terrain.libelle_surface}</span>
                      </div>
                    </div>

                    {/* Google Maps */}
                    <div className="h-64 bg-gray-800 rounded-b-3xl overflow-hidden">
                      {generateGoogleMapsUrl(terrain) ? (
                        <iframe
                          src={generateGoogleMapsUrl(terrain)}
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title={`Carte de ${terrain.name}`}
                          className="rounded-b-3xl"
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center bg-gray-800/50 backdrop-blur-sm">
                          <div className="text-center p-4">
                            <FaMapMarkerAlt className="text-gray-400 mx-auto mb-2 text-4xl" />
                            <p className="text-gray-400 text-sm">
                              Carte non disponible<br />
                              <span className="text-xs">API key Google Maps requise</span>
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Club;